import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  X, Building2, User, Mail, MapPin, Hash, FileText, Calendar, PenLine,
  Phone, Loader2, Send, Check, Globe, Network, Box
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api, formatApiError } from '../lib/api';
import Flag from './Flag';
import RegulationsPanel from './RegulationsPanel';
import TurnstileWidget from './TurnstileWidget';

const TYPE_LABEL = {
  did: 'DID Number',
  tollfree: 'Toll Free Number',
  uifn: 'UIFN Number'
};

const TYPE_COLOR = {
  did: 'from-violet-500 to-purple-600',
  tollfree: 'from-fuchsia-500 to-pink-500',
  uifn: 'from-violet-600 to-fuchsia-600'
};

// DID order: Kind of number multi-checkbox + Associated pricelist single-select
const KINDS = ['DID', 'National', '2-Way', 'UC', 'Mobile'];
const PRICELISTS = ['Standard', 'Operator', 'Special'];

const blankCompany = {
  company: '', firstName: '', surname: '', email: '', phone: '',
  address: '', postalCode: '', city: '', country: '', vat: '', serviceDescription: ''
};

const blankDid = {
  kinds: [], cityRegion: '', pricelist: '', sipTrunk: '',
  date: new Date().toISOString().slice(0, 10), agree: false
};

const blankSimple = {
  serviceDescription: '', cityRegion: '', sipTrunk: '',
  date: new Date().toISOString().slice(0, 10), agree: false
};

export default function OrderFormDialog({ open, onClose, type, row }) {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [company, setCompany] = useState(blankCompany);
  const [order, setOrder] = useState(blankDid);
  const [tsToken, setTsToken] = useState(null);
  const onVerify = useCallback((token) => setTsToken(token), []);

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setCompany(blankCompany);
      setOrder(type === 'did' ? blankDid : blankSimple);
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, type]);

  if (!open || !type || !row) return null;

  const updateCompany = (k, v) => setCompany((c) => ({ ...c, [k]: v }));
  const updateOrder = (k, v) => setOrder((o) => ({ ...o, [k]: v }));
  const toggleKind = (k) =>
    setOrder((o) => ({
      ...o,
      kinds: o.kinds.includes(k) ? o.kinds.filter((x) => x !== k) : [...o.kinds, k]
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!company.company || !company.firstName || !company.surname || !company.email) {
      toast({ title: 'Required fields missing', description: 'Please fill in company, name, surname and email.' });
      return;
    }
    if (!order.cityRegion || !order.sipTrunk) {
      toast({ title: 'Order details missing', description: 'Please fill in City/Region and SIP Trunk.' });
      return;
    }
    if (!order.agree) {
      toast({ title: 'Authorisation required', description: 'Please confirm you are authorised to sign the order.' });
      return;
    }
    if (!tsToken) {
      toast({ title: 'Verification required', description: 'Please complete the bot check.' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        number_type: type,
        country_name: row.country,
        country_code: row.code,
        package_name: row.variant || null,
        monthly_fee: row.mrc != null ? `£${Number(row.mrc).toFixed(2)}` : null,
        setup_fee: row.nrc != null ? `£${Number(row.nrc).toFixed(2)}` : null,
        channels: order.kinds?.join(', ') || null,
        company_name: company.company,
        contact_name: `${company.firstName} ${company.surname}`.trim(),
        email: company.email,
        phone: company.phone || '',
        address: [company.address, company.postalCode, company.city, company.country].filter(Boolean).join(', '),
        vat_number: company.vat || '',
        kyc_notes: `Service description: ${company.serviceDescription || '-'}\nCity/Region: ${order.cityRegion}\nSIP Trunk: ${order.sipTrunk}\nPricelist: ${order.pricelist || '-'}\nDate: ${order.date}`,
        turnstile_token: tsToken,
      };
      await api.post('/api/public/number-order', payload);
      setSubmitted(true);
      toast({ title: 'Order received', description: 'Our team will confirm your order within 24 hours.' });
    } catch (err) {
      toast({ title: 'Submission failed', description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm animate-fade-in-up"
        style={{ animationDuration: '0.2s' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-5xl bg-white rounded-3xl shadow-2xl my-4 md:my-8 overflow-hidden animate-fade-in-up"
        style={{ animationDuration: '0.25s' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`relative bg-gradient-to-br ${TYPE_COLOR[type]} text-white p-5 md:p-7`}>
          <div className="absolute inset-0 dot-pattern opacity-25" />
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X size={18} />
          </button>
          <div className="relative flex items-center gap-4">
            <Flag code={row.code} size="xl" className="border-white/30 bg-white/15 backdrop-blur-sm" />
            <div>
              <p className="text-xs uppercase tracking-widest text-white/75">Order Form</p>
              <h2 className="text-2xl md:text-3xl font-extrabold mt-0.5">{row.country}</h2>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/15 text-xs font-semibold">
                  <Box size={12} /> {TYPE_LABEL[type]}
                </span>
                {row.variant && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/15 text-xs font-semibold">
                    {row.variant}
                  </span>
                )}
                {row.poa ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-500/30 text-xs font-semibold border border-amber-300/40">
                    Price on Application
                  </span>
                ) : (
                  row.mrc != null && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/15 text-xs font-semibold">
                      £{row.mrc.toFixed(2)} / month
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        {submitted ? (
          <div className="p-10 md:p-14 text-center">
            <div className="w-16 h-16 rounded-full bg-violet-50 text-violet-600 mx-auto mb-5 flex items-center justify-center">
              <Check size={30} />
            </div>
            <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 mb-3">
              Order received, {company.firstName || 'thank you'}
            </h3>
            <p className="text-slate-600 max-w-xl mx-auto">
              We will confirm your order for the {row.country} {TYPE_LABEL[type]} and contact you on
              <span className="font-semibold text-violet-700"> {company.email}</span> within 24 business hours.
            </p>
            <button onClick={onClose} className="btn-primary mt-7 mx-auto">Close</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="grid lg:grid-cols-2 gap-0">
            {/* REGULATIONS PANEL (full width, top) */}
            <div className="lg:col-span-2 p-5 md:p-7 border-b border-slate-100 bg-white">
              <RegulationsPanel country={row.country} type={type} code={row.code} compact />
            </div>

            {/* LEFT: Company details */}
            <div className="p-5 md:p-7 lg:border-r border-slate-100 bg-slate-50/40">
              <Heading icon={Building2} text="Please fill in YOUR company details" />
              <div className="grid grid-cols-2 gap-3 md:gap-4 mt-5">
                <Field label="Company *" full>
                  <input value={company.company} onChange={(e) => updateCompany('company', e.target.value)} className={inp} placeholder="Acme Ltd" />
                </Field>
                <Field label="First name *">
                  <input value={company.firstName} onChange={(e) => updateCompany('firstName', e.target.value)} className={inp} placeholder="Jane" />
                </Field>
                <Field label="Surname *">
                  <input value={company.surname} onChange={(e) => updateCompany('surname', e.target.value)} className={inp} placeholder="Smith" />
                </Field>
                <Field label="Email *" full>
                  <input type="email" value={company.email} onChange={(e) => updateCompany('email', e.target.value)} className={inp} placeholder="jane@acme.com" />
                </Field>
                <Field label="Phone">
                  <input value={company.phone} onChange={(e) => updateCompany('phone', e.target.value)} className={inp} placeholder="+44 ..." />
                </Field>
                <Field label="Country">
                  <input value={company.country} onChange={(e) => updateCompany('country', e.target.value)} className={inp} placeholder="United Kingdom" />
                </Field>
                <Field label="Address" full>
                  <input value={company.address} onChange={(e) => updateCompany('address', e.target.value)} className={inp} placeholder="Street, building..." />
                </Field>
                <Field label="Postal code / ZIP">
                  <input value={company.postalCode} onChange={(e) => updateCompany('postalCode', e.target.value)} className={inp} placeholder="IG6 2SQ" />
                </Field>
                <Field label="City">
                  <input value={company.city} onChange={(e) => updateCompany('city', e.target.value)} className={inp} placeholder="London" />
                </Field>
                <Field label="VAT / Company No." full>
                  <input value={company.vat} onChange={(e) => updateCompany('vat', e.target.value)} className={inp} placeholder="GB123456789" />
                </Field>
                <Field label="Service description" full>
                  <textarea
                    rows={2}
                    value={company.serviceDescription}
                    onChange={(e) => updateCompany('serviceDescription', e.target.value)}
                    className={`${inp} resize-none`}
                    placeholder="Briefly describe how this number will be used..."
                  />
                </Field>
              </div>
            </div>

            {/* RIGHT: Order details */}
            <div className="p-5 md:p-7">
              <Heading icon={FileText} text="Order" />

              {type === 'did' ? (
                <DidOrderFields order={order} update={updateOrder} toggleKind={toggleKind} />
              ) : (
                <SimpleOrderFields order={order} update={updateOrder} type={type} />
              )}

              {/* Signature / agreement */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <PenLine size={14} className="text-violet-500" /> Signature
                </p>
                <label className="flex items-start gap-3 p-4 rounded-xl bg-violet-50 border border-violet-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={order.agree}
                    onChange={(e) => updateOrder('agree', e.target.checked)}
                    className="mt-1 w-4 h-4 accent-violet-600"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    I hereby confirm that I am duly authorised to sign this order form on behalf of the
                    company and to legally bind the company. I have read, understood and agreed to comply
                    with the applicable Code of Conduct and laws for the country in which the service is
                    requested.
                  </span>
                </label>

                <div className="mt-4 flex justify-center">
                  <TurnstileWidget onVerify={onVerify} />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="lg:col-span-2 border-t border-slate-100 bg-slate-50/60 p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <p className="text-[11px] text-slate-500 max-w-2xl leading-relaxed">
                All quotations, prices and deliveries are subject exclusively to our General Terms and
                Conditions of Delivery, which form an integral part of all our contracts. By submitting
                this Order Form, you accept these terms.
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <button type="button" onClick={onClose} className="btn-light">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-primary">
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Submit order <Send size={16} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------- ORDER FIELDS BY TYPE ---------- */

function DidOrderFields({ order, update, toggleKind }) {
  return (
    <div className="space-y-5 mt-5">
      <Field label="Kind of number" hint="Select all that apply">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => {
            const active = order.kinds.includes(k);
            return (
              <button
                type="button"
                key={k}
                onClick={() => toggleKind(k)}
                className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${
                  active
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md shadow-violet-500/30'
                    : 'bg-white text-slate-700 border border-slate-200 hover:border-violet-300'
                }`}
              >
                {k}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="City / Region *" icon={MapPin}>
        <input
          value={order.cityRegion}
          onChange={(e) => update('cityRegion', e.target.value)}
          className={inp}
          placeholder="e.g. London, +44 207"
        />
      </Field>

      <Field label="Associated pricelist">
        <div className="flex flex-wrap gap-2">
          {PRICELISTS.map((p) => {
            const active = order.pricelist === p;
            return (
              <button
                type="button"
                key={p}
                onClick={() => update('pricelist', p)}
                className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${
                  active
                    ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md shadow-violet-500/30'
                    : 'bg-white text-slate-700 border border-slate-200 hover:border-violet-300'
                }`}
              >
                {p}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="SIP Trunk *" icon={Network}>
        <input
          value={order.sipTrunk}
          onChange={(e) => update('sipTrunk', e.target.value)}
          className={inp}
          placeholder="Trunk name or IP / FQDN"
        />
      </Field>

      <Field label="Date" icon={Calendar}>
        <input
          type="date"
          value={order.date}
          onChange={(e) => update('date', e.target.value)}
          className={inp}
        />
      </Field>
    </div>
  );
}

function SimpleOrderFields({ order, update, type }) {
  return (
    <div className="space-y-5 mt-5">
      <Field label="Service Description *" icon={FileText} hint="Tell us what the number will be used for">
        <textarea
          rows={3}
          value={order.serviceDescription}
          onChange={(e) => update('serviceDescription', e.target.value)}
          className={`${inp} resize-none`}
          placeholder={
            type === 'tollfree'
              ? 'e.g. Inbound customer support hotline routed to our IVR'
              : 'e.g. Single global freephone for international support'
          }
        />
      </Field>

      <Field label="City / Region *" icon={MapPin}>
        <input
          value={order.cityRegion}
          onChange={(e) => update('cityRegion', e.target.value)}
          className={inp}
          placeholder={type === 'tollfree' ? 'e.g. National 0800 / specific city' : 'Primary serving region'}
        />
      </Field>

      <Field label="SIP Trunk *" icon={Network}>
        <input
          value={order.sipTrunk}
          onChange={(e) => update('sipTrunk', e.target.value)}
          className={inp}
          placeholder="Trunk name or IP / FQDN"
        />
      </Field>

      <Field label="Date" icon={Calendar}>
        <input
          type="date"
          value={order.date}
          onChange={(e) => update('date', e.target.value)}
          className={inp}
        />
      </Field>
    </div>
  );
}

/* ---------- HELPERS ---------- */

const inp =
  'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none text-sm bg-white transition-all';

function Field({ label, icon: Icon, hint, children, full }) {
  return (
    <label className={`block ${full ? 'col-span-2' : ''}`}>
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-violet-500" />} {label}
      </span>
      {children}
      {hint && <span className="text-[11px] text-slate-400 mt-1 block">{hint}</span>}
    </label>
  );
}

function Heading({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
      <span className="w-8 h-8 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center">
        <Icon size={16} />
      </span>
      <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700">{text}</h3>
    </div>
  );
}
